import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useAuthStore } from '@/stores/authStore';
import { RouteMap } from '@/routes/Routes';
import { useOrganizationNotes } from '../hooks/useOrganizationNotes';
import { useOrganizations } from '../hooks/useOrganizations';
import { useOrganizationFolders } from '../hooks/useOrganizationFolders';
import { BETA_ORGANIZATIONS_FEATURE_ENABLED } from '../config';
import FolderShareModal from '../components/FolderShareModal';
import type { OrganizationFolder } from '../types';
import useAlertStore from '@/stores/alertStore';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { useBannerStore } from '@/stores/bannerStore';
import { useSearchFilterNotesStore } from '@/stores/searchFilterNotesStore';
import NotesContent from '@/pages/MyNotes/components/NotesContent/NotesContent';

const OrganizationNotesPage = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userData } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);
  const { headerHeight } = useBannerStore();
  const resetFilters = useSearchFilterNotesStore((state) => state.resetFilters);

  useEffect(() => {
    resetFilters();
  }, [organizationId, resetFilters]);

  // Folder selection is URL-driven (?folder=<id>) so the sidebar folder tree can
  // deep-link straight into a folder. Absent param = 'general' (notes not filed
  // in any folder — visible to the whole team). There is no "all notes" view;
  // folders gate access, so notes live in General or in a specific folder.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFolderId = searchParams.get('folder') ?? 'general';
  const setSelectedFolderId = (folderId: string) => {
    setSearchParams(folderId === 'general' ? {} : { folder: folderId }, { replace: true });
  };
  // Folder modal: undefined target = create mode, a folder = edit mode.
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState<OrganizationFolder | undefined>(undefined);

  const [joinWarning, setJoinWarning] = useState<string | null>(() =>
    organizationId ? sessionStorage.getItem(`orgJoinWarning:${organizationId}`) : null,
  );

  useEffect(() => {
    if (!organizationId) {
      setJoinWarning(null);
      return;
    }
    setJoinWarning(sessionStorage.getItem(`orgJoinWarning:${organizationId}`));
  }, [organizationId]);

  const dismissJoinWarning = () => {
    if (organizationId) {
      sessionStorage.removeItem(`orgJoinWarning:${organizationId}`);
    }
    setJoinWarning(null);
  };

  const {
    organization,
    notes,
    isLoading,
    isLoadingOrg,
    orgError,
    error: notesError,
    refetch,
  } = useOrganizationNotes(organizationId, BETA_ORGANIZATIONS_FEATURE_ENABLED);

  const { removeNote, leaveOrganization, isMutating } = useOrganizations(BETA_ORGANIZATIONS_FEATURE_ENABLED);

  const {
    folders,
    removeNoteFromFolder,
    refetch: refetchFolders,
  } = useOrganizationFolders(organizationId, BETA_ORGANIZATIONS_FEATURE_ENABLED);

  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);

  const isOwner = organization?.owner_id === userData?.firebase_uid;
  // Emails are stored lowercased (AddMemberModal lowercases on invite), so every
  // comparison must lowercase too — a raw compare locks `John.Doe@Example.com`
  // out of their own team via the non-member redirect below.
  const currentEmail = (userData?.email ?? '').toLowerCase();
  const isMember = (organization?.member_emails ?? []).some((email) => email.toLowerCase() === currentEmail);
  // Admins are stored as lowercased emails on the org and manage team folders like the owner.
  const isAdmin = (organization?.admin_emails ?? []).includes(currentEmail);
  const canManageTeam = isOwner || isAdmin;

  // The currently selected folder object (if a real folder is selected).
  const selectedFolder = folders.find((f) => f.id === selectedFolderId);
  const canManageSelectedFolder =
    !!selectedFolder && ((selectedFolder.created_by_email ?? '').toLowerCase() === currentEmail || canManageTeam);

  // Note ids not filed in any folder ("General").
  const notFiledNoteIds = useMemo(() => {
    const orgNoteIds = organization?.note_ids ?? [];
    return orgNoteIds.filter((id) => !folders.some((f) => f.note_ids.includes(id)));
  }, [organization?.note_ids, folders]);

  // Filter passed to NotesContent. 'general' = notes not filed in any folder;
  // a folder id = that folder's notes.
  //
  // NOTE: `folders` only contains folders THIS user can access, so a note filed
  // in a restricted folder is not subtracted out and lands in the General filter.
  // That is fail-open at the filter level; what actually keeps it off screen is
  // the per-note 403 in useOrganizationNotes, which is deliberately treated as
  // "skip this note". Don't remove that 403 handling (CONTRACT #9 threat model).
  const noteIdFilter = useMemo(() => {
    if (selectedFolderId === 'general') return notFiledNoteIds;
    return selectedFolder?.note_ids ?? [];
  }, [selectedFolderId, notFiledNoteIds, selectedFolder]);

  // Header title reflects the folder you're in (or General), not the team name.
  // Falls back to the team name only while the folder is still loading.
  const viewLabel = selectedFolderId === 'general' ? 'General' : (selectedFolder?.name ?? organization?.name ?? 'Team');

  if (!BETA_ORGANIZATIONS_FEATURE_ENABLED) {
    return <Navigate to={RouteMap.HOME} replace />;
  }

  if (!organizationId) {
    return <Navigate to={RouteMap.ORGANIZATIONS} replace />;
  }

  // Redirect non-members as soon as org loads (don't wait for notes).
  // This also covers non-beta users who aren't invited to this org — they get
  // bounced home rather than to /beta, since beta status no longer gates access.
  if (!isLoadingOrg && (orgError || (organization && !isMember))) {
    return <Navigate to={RouteMap.HOME} replace />;
  }

  const handleOpenEditFolder = (folder: OrganizationFolder) => {
    setFolderToEdit(folder);
    setFolderModalOpen(true);
  };

  const handleFolderSaved = async () => {
    // If the folder we were viewing/editing was deleted, drop back to General.
    if (folderToEdit && selectedFolderId === folderToEdit.id) {
      setSelectedFolderId('general');
    }
    await refetchFolders();
  };

  const handleLeaveTeam = async () => {
    if (!organizationId) return;

    try {
      await leaveOrganization(organizationId);
      showAlert('You have left the team.', 'success');
      setLeaveConfirmOpen(false);
      navigate(RouteMap.ORGANIZATIONS);
    } catch (err: unknown) {
      const errorObj = err as { detail?: unknown };
      const detail = typeof errorObj.detail === 'string' ? errorObj.detail : '';
      showAlert(detail || 'Could not leave team. Please try again.', 'error');
      setLeaveConfirmOpen(false);
    }
  };

  const handleRemoveNote = async (noteId: string) => {
    if (!organizationId) return;

    try {
      await removeNote({ organizationId, noteId });
      // Best-effort: clear folder membership so stale IDs can't reappear if re-shared.
      const foldersWithNote = folders.filter((f) => f.note_ids.includes(noteId));
      await Promise.allSettled(
        foldersWithNote.map((folder) => removeNoteFromFolder({ organizationId, folderId: folder.id, noteId })),
      );
      if (foldersWithNote.length > 0) refetchFolders();
      showAlert('Note removed from team.', 'success');
      refetch();
    } catch (error) {
      showAlert(getApiErrorMessage(error, 'Failed to remove note. Please try again.'), 'error');
    }
  };

  return (
    <Stack
      alignItems="center"
      pt={4}
      pb={0}
      px={isMobile ? 2 : 4}
      height={`calc(100vh - ${headerHeight}px)`}
      minWidth={0}
      overflow="hidden"
    >
      <Stack
        width="100%"
        maxWidth="1620px"
        direction={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'flex-start' : 'center'}
        justifyContent="space-between"
        gap={isMobile ? 2 : 0}
        flexShrink={0}
      >
        <Stack gap={0.5} alignItems="flex-start">
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="h5" fontWeight="700">
              {viewLabel}
            </Typography>
            {canManageSelectedFolder && selectedFolder && (
              <IconButton
                size="small"
                aria-label={`Manage ${selectedFolder.name}`}
                onClick={() => handleOpenEditFolder(selectedFolder)}
              >
                <SettingsOutlinedIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {isMember && !isOwner && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={() => setLeaveConfirmOpen(true)}
              disabled={isMutating}
            >
              Leave Team
            </Button>
          )}
        </Stack>
      </Stack>

      {joinWarning === 'active_paid_plan' && (
        <Box width="100%" maxWidth="1620px" mt={3}>
          <Alert
            severity="warning"
            action={
              <Stack direction="row" spacing={1} alignItems="center">
                <Button color="inherit" size="small" onClick={() => navigate(RouteMap.PLAN)}>
                  Manage billing
                </Button>
                <IconButton color="inherit" size="small" onClick={dismissJoinWarning} aria-label="Dismiss">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            }
          >
            You still have an active paid plan. Cancel it in Settings → Plan to avoid duplicate charges while using this
            team.
          </Alert>
        </Box>
      )}

      {/* Distinguish "this team has no notes yet" from "we could not load them".
          Showing the friendly empty state on a load failure reads as data loss. */}
      {!isLoading && notesError && (
        <Box width="100%" maxWidth="1620px" mt={3}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void refetch()}>
                Retry
              </Button>
            }
          >
            We couldn&apos;t load this team&apos;s notes. They haven&apos;t been deleted — please try again.
          </Alert>
        </Box>
      )}

      {!isLoading && !notesError && notes.length === 0 && isOwner && (
        <Box width="100%" maxWidth="1620px" mt={3}>
          <Alert severity="info">
            Notes you share with this team will appear here. Use &quot;Move to folder&quot; on a note in My Notes to add
            one.
          </Alert>
        </Box>
      )}

      <Box width="100%" maxWidth="1620px" mt={3} flexShrink={0} />

      <NotesContent
        organizationId={organizationId}
        onRemoveFromOrganization={handleRemoveNote}
        canRemoveFromOrganization={canManageTeam}
        noteIdFilter={noteIdFilter}
        folderName={viewLabel}
      />

      <Dialog
        open={leaveConfirmOpen}
        onClose={() => (isMutating ? null : setLeaveConfirmOpen(false))}
        aria-labelledby="leave-team-dialog-title"
        aria-describedby="leave-team-dialog-description"
      >
        <DialogTitle id="leave-team-dialog-title">Leave Team?</DialogTitle>
        <DialogContent>
          <DialogContentText id="leave-team-dialog-description">
            Are you sure you want to leave &quot;{organization?.name}&quot;? You will lose access to its notes.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveConfirmOpen(false)} disabled={isMutating}>
            Cancel
          </Button>
          <Button onClick={handleLeaveTeam} color="error" variant="contained" disabled={isMutating} autoFocus>
            Leave Team
          </Button>
        </DialogActions>
      </Dialog>

      {organization && (
        <FolderShareModal
          open={folderModalOpen}
          onClose={() => setFolderModalOpen(false)}
          organizationId={organization.id}
          memberEmails={organization.member_emails}
          ownerEmail={organization.owner_email}
          folder={folderToEdit}
          onSaved={handleFolderSaved}
        />
      )}
    </Stack>
  );
};

export default OrganizationNotesPage;
